import React, { useRef, useState, useMemo, useCallback, useEffect, useImperativeHandle, forwardRef } from "react";
import { EditOutlined } from "@ant-design/icons";
import { Drawer, Form, Button, Col, Row, Input, message, Select, Divider, Spin, Checkbox } from "antd";
import { connect } from "dva";
import { useStates } from "@/utils/address";
import { RoleType } from "@/utils/enum";
import { getUrlParams, DEFAULT_ID, hideJiraWidget, showJiraWidget } from "@/utils/utils";
import { Validator } from "@/utils/validator";
import { getAllUsersRoles, getAllAdminRoles } from "@/services/roles";
import useScreenDetector from "@/hooks/useScreenDetector";
import EmailInput from "@/components/EmailInput";
import PhoneNumberInput, { flatPhoneNumber } from "@/components/PhoneNumberInput";
import AddressInput from "../AddressInput";

const { Option } = Select;

const EditUser = forwardRef(
  ({ dispatch, disabled, userData, entities, companyId: paramCompanyId = null, showButton = true, onSuccess }, ref) => {
    const editUserFormRef = useRef(null);

    const [visible, setVisible] = useState(false);
    const [rolesList, setRolesList] = useState([]);
    const [loadingRoles, setLoadingRoles] = useState(false);

    const { loading: loadingStates, states } = useStates(!visible);

    const { isMobile } = useScreenDetector();

    const userId = useMemo(() => userData?.userId ?? userData?.id ?? getUrlParams("userId"), [userData]);

    const companyId = useMemo(() => paramCompanyId ?? entities?.items?.at(0)?.companyId, [entities, paramCompanyId]);

    const editingAdminUser = useMemo(() => window.location.pathname.includes(`/admin/adminusers/view`), []);

    const showDrawer = useCallback(() => {
      setVisible(true);
      hideJiraWidget();
    }, []);

    const closeDrawer = useCallback(() => {
      setVisible(false);
      showJiraWidget();
    }, []);

    const onSubmit = useCallback(() => {
      const values = editUserFormRef.current.getFieldValue();
      editUserFormRef?.current?.validateFields().then(result => {
        const payload = {
          firstName: result.firstName,
          lastName: result.lastName,
          email: result.email,
          phoneNumber: flatPhoneNumber(result.phone),
          phoneNumberExtension: result.phoneExtension,
          additionalPhoneNumber: flatPhoneNumber(result.additionalPhone),
          additionalPhoneNumberExtension: result.additionalPhoneExtension,
          addressLine1: result.addressLine1,
          addressLine2: result.addressLine2,
          city: result.city,
          stateId: result.stateId,
          shortStateName: values.stateShortName, // Form Hidden values are not retrieved via validateFields
          zipCode: result.zipCode,
          hasTWIC: !!result.hasTWIC,
          hasEModal: !!result.hasEModal,
          isVerified: result.isVerified,
          emailConfirmed: result.emailConfirmed,
          canSkipVehicle: userData.canSkipVehicle,
          roleIds: result.roles,
          companyId,
          type: userData.type,
        };

        const type = editingAdminUser ? "adminUser/editSingleUser" : "singleuser/editSingleUser";

        const hide = message.loading("Saving changes...");
        dispatch({
          type,
          userId,
          payload,
        }).then(response => {
          hide();

          if (response?.error) {
            message.error("Something went wrong editing the user");
            return;
          }

          editUserFormRef?.current?.resetFields();
          setVisible(false);
          showJiraWidget();
          message.success("User updated successfully!");

          onSuccess?.();
        });
      });
    }, [dispatch, userData, userId, companyId, editingAdminUser, onSuccess]);

    useImperativeHandle(ref, () => ({
      showDrawer,
      closeDrawer,
    }));

    useEffect(() => {
      if (!visible) {
        return;
      }

      setLoadingRoles(true);

      const endpoint = () => (editingAdminUser ? getAllAdminRoles() : getAllUsersRoles(userData.type));

      endpoint().then(res => {
        if (Array.isArray(res)) {
          setRolesList(res);
        }

        setLoadingRoles(false);
      });
    }, [editingAdminUser, visible, userData?.type]);

    useEffect(() => {
      if (!userData || !visible || loadingRoles || loadingStates || states.length === 0) {
        return;
      }

      const { stateId } = userData;
      const stateObj = states.find(state => state.id === stateId);

      editUserFormRef?.current?.setFieldsValue({
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        phone: userData.phoneNumber,
        phoneExtension: userData.phoneNumberExtension,
        additionalPhone: userData.additionalPhoneNumber ?? "",
        additionalPhoneExtension: userData.additionalPhoneNumberExtension,
        addressLine1: userData.addressLine1,
        addressLine2: userData.addressLine2,
        stateId: stateId !== DEFAULT_ID ? stateId : null,
        stateShortName: stateObj?.shortName || null,
        stateName: stateObj?.name || null,
        city: userData.city,
        zipCode: userData.zipCode,
        isVerified: userData.isVerified,
        emailConfirmed: userData.emailConfirmed,
        canSkipVehicle: userData.canSkipVehicle,
        roles: (userData.roles || []).map(role => role.id),
        hasTWIC: userData.hasTWIC,
        hasEModal: userData.hasEModal,
      });
    }, [userData, states, visible, loadingRoles, loadingStates]);

    return (
      <div style={{ marginRight: 8, display: "inline-block" }}>
        {showButton && (
          <Button
            type="primary"
            disabled={disabled}
            onClick={showDrawer}
            style={{ borderColor: "white", margin: "8px 0" }}
            icon={<EditOutlined />}
          >
            Edit User
          </Button>
        )}
        <Drawer
          title="Edit User"
          width={isMobile ? "80%" : "40%"}
          ref={ref}
          onClose={closeDrawer}
          open={visible}
          bodyStyle={{ paddingBottom: 80 }}
          footer={
            <div style={{ textAlign: "right" }}>
              <Button onClick={closeDrawer} style={{ marginRight: 8 }}>
                Cancel
              </Button>
              <Button onClick={onSubmit} type="primary">
                Submit
              </Button>
            </div>
          }
        >
          <Spin spinning={loadingStates || loadingRoles}>
            <Form ref={editUserFormRef} layout="vertical">
              <Row gutter={16}>
                <Col lg={12}>
                  <Form.Item name="firstName" label="First Name" rules={[Validator.required("First Name is required")]}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col lg={12}>
                  <Form.Item name="lastName" label="Last Name" rules={[Validator.required("Last Name is required")]}>
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col lg={24}>
                  <EmailInput formItemProps={{ required: true }} required />
                </Col>
              </Row>
              <Row gutter={16}>
                <Col lg={12}>
                  <PhoneNumberInput
                    formItemProps={{
                      name: "phone",
                      label: "Phone Number",
                      extensionName: "phoneExtension",
                      formRef: editUserFormRef,
                      rules: [Validator.required("Phone Number is required")],
                    }}
                  />
                </Col>
                <Col lg={12}>
                  <PhoneNumberInput
                    formItemProps={{
                      name: "additionalPhone",
                      label: "Add'l. Phone Number",
                      formRef: editUserFormRef,
                      extensionName: "additionalPhoneExtension",
                    }}
                  />
                </Col>
              </Row>

              <Row gutter={16}>
                <Col lg={24}>
                  <AddressInput
                    formReference={editUserFormRef}
                    loadingStates={loadingStates}
                    states={states}
                    addressLine1FormItemProps={{
                      name: "addressLine1",
                      label: "Address line 1",
                      rules: [Validator.optional(Validator.required, "Address line 1 is required")],
                    }}
                    stateIdFormItemProps={{
                      name: "stateId",
                      label: "State",
                      rules: [Validator.optional(Validator.required, "State is required")],
                    }}
                    stateNameFormItemProps={{
                      name: "stateName",
                      rules: [Validator.optional(Validator.required, "State is required")],
                    }}
                    stateShortNameFormItemProps={{
                      name: "shortStateName",
                      rules: [Validator.optional(Validator.required, "State is required")],
                    }}
                    cityFormItemProps={{
                      name: "city",
                      label: "City",
                      rules: [Validator.optional(Validator.required, "City is required")],
                    }}
                    zipCodeFormItemProps={{
                      name: "zipCode",
                      label: "Zip code",
                      rules: [Validator.optional(Validator.required, "Zip code is required")],
                    }}
                  />
                </Col>
              </Row>

              <Row gutter={16}>
                <Col lg={24}>
                  <Form.Item name="roles" label="Roles" rules={[Validator.required("Please select at least one role")]}>
                    <Select
                      placeholder="Choose roles"
                      mode="multiple"
                      filterOption={(input, option) => option.key.toLowerCase().indexOf(input.toLowerCase()) !== -1}
                      getPopupContainer={trigger => trigger.parentElement}
                    >
                      {rolesList.map(rol => (
                        <Option key={`${rol.id}${rol.name}`} value={rol.id}>
                          {rol.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              {!editingAdminUser && (
                <>
                  <Row gutter={16}>
                    <Col lg={8}>
                      <Form.Item name="emailConfirmed" valuePropName="checked">
                        <Checkbox>Email Verified</Checkbox>
                      </Form.Item>
                    </Col>
                    <Col lg={8}>
                      <Form.Item name="isVerified" valuePropName="checked">
                        <Checkbox>Autosled Verified</Checkbox>
                      </Form.Item>
                    </Col>
                  </Row>
                  {userData?.type === RoleType.Transporter && (
                    <>
                      <Divider plain orientation="left" style={{ marginTop: 4 }}>
                        Credentials
                      </Divider>
                      <Row gutter={16}>
                        <Col lg={8}>
                          <Form.Item name="hasTWIC" valuePropName="checked">
                            <Checkbox>Has TWIC</Checkbox>
                          </Form.Item>
                        </Col>
                        <Col lg={8}>
                          <Form.Item name="hasEModal" valuePropName="checked">
                            <Checkbox>Has eModal</Checkbox>
                          </Form.Item>
                        </Col>
                      </Row>
                    </>
                  )}
                </>
              )}
            </Form>
          </Spin>
        </Drawer>
      </div>
    );
  },
);

EditUser.displayName = "EditUser";

export default connect(
  ({ singleuser, adminUser, entityUsers, loading }) => ({
    singleuser,
    adminUser,
    entities: entityUsers.entities,
    loading: loading.effects["adminUser/editSingleUser"] || loading.effects["singleuser/editSingleUser"],
  }),
  null,
  null,
  { forwardRef: true },
)(EditUser);
